import { Main } from 'styles/themeColors';
import {
  CreditTrackingMode,
  AddressLines,
  ConnectionStatusConfig,
  CreditInfo,
} from 'types/credit.types';
import {
  CREDIT_STATUS_MESSAGES,
  CREDIT_SOURCES
} from 'constants/credit';
import { emptyCreditInfo } from 'data/VendorCompany';
import { currencyFormatter } from './formatters';

/**
 * Formats MDM address string into structured address lines
 * @param addressString - Raw address string from MDM
 * @returns Formatted address lines object
 */
export const formatMdmAddress = (addressString: string | undefined): AddressLines => {
  if (!addressString) {
    return { line1: '', line2: '', line3: '' };
  }

  // Split the address by commas
  const parts = addressString.split(',').map(part => part.trim());

  if (parts.length >= 5) {
    // Format: "1251 NW BRIARCLIFF PKWY STE 700, KANSAS CITY, Missouri, 64116, United States"
    return {
      line1: parts[0], // Street address
      line2: `${parts[1]}, ${parts[2]}`, // City, State
      line3: `${parts[3]}, ${parts[4]}` // Zip, Country
    };
  }

  // Fallback if format is different
  return {
    line1: parts[0] || '',
    line2: parts.length > 1 ? parts[1] : '',
    line3: parts.length > 2 ? parts.slice(2).join(', ') : ''
  };
};

/**
 * Gets credit tracking status message based on mode and system state
 * @param creditTrackingMode - Current credit tracking mode
 * @param isSystemOffline - Whether the system is offline
 * @returns Status message string
 */
export const getCreditStatus = (
  creditTrackingMode: CreditTrackingMode,
  isSystemOffline: boolean = false
): string => {
  switch (creditTrackingMode) {
    case CreditTrackingMode.OFMP:
      return CREDIT_STATUS_MESSAGES.TRACKED_BY_OFMP;
    case CreditTrackingMode.ERP:
      return isSystemOffline
        ? CREDIT_STATUS_MESSAGES.SOURCE_FAILED
        : CREDIT_STATUS_MESSAGES.READING_FROM_SOURCE;
    case CreditTrackingMode.OFFLINE:
      return CREDIT_STATUS_MESSAGES.SOURCE_FAILED;
    case CreditTrackingMode.UID_REQUIRED:
    default:
      return CREDIT_STATUS_MESSAGES.UID_REQUIRED;
  }
};

/**
 * Gets background color for credit status badge
 * @param creditTrackingMode - Current credit tracking mode
 * @param isSystemOffline - Whether the system is offline
 * @returns Background color string
 */
export const getCreditStatusBgColor = (
  creditTrackingMode: CreditTrackingMode,
  isSystemOffline: boolean = false
): string => {
  switch (creditTrackingMode) {
    case CreditTrackingMode.OFMP:
      return Main.success;
    case CreditTrackingMode.ERP:
      return isSystemOffline ? Main.warning : Main.success;
    case CreditTrackingMode.OFFLINE:
      return Main.warning;
    case CreditTrackingMode.UID_REQUIRED:
      return Main.error;
    default:
      return Main.warning;
  }
};

/**
 * Gets connection status configuration
 * @param creditTrackingMode - Current credit tracking mode
 * @param creditInfo - Credit information with MDM details
 * @returns Connection status configuration
 */
export const getConnectionStatusConfig = (
  creditTrackingMode: CreditTrackingMode,
  creditInfo?: CreditInfo
): ConnectionStatusConfig => {
  const isSystemOffline = creditTrackingMode === CreditTrackingMode.OFFLINE ||
    (creditTrackingMode === CreditTrackingMode.ERP &&
      creditInfo?.companyMdmDetails?.idStatus?.toUpperCase() !== 'ACTIVE');

  const status = isSystemOffline
    ? CREDIT_STATUS_MESSAGES.SOURCE_OFFLINE
    : CREDIT_STATUS_MESSAGES.SOURCE_CONNECTED;

  const backgroundColor = isSystemOffline
    ? Main.warning
    : Main.success;

  const color = isSystemOffline
    ? Main.black
    : Main.white;

  return { status, backgroundColor, color };
};

/**
 * Calculates credit utilization
 * @param totalLimit - Total credit limit
 * @param availableLimit - Available credit limit
 * @returns Formatted utilization string
 */
export const calculateUtilization = (totalLimit: number, availableLimit: number): string => {

  return totalLimit !== availableLimit
    ? currencyFormatter.format(totalLimit - availableLimit)
    : currencyFormatter.format(0);

};

/**
 * Checks if the system is offline based on credit info
 * @param creditInfo - Credit information
 * @returns Boolean indicating if system is offline
 */
export const isSystemOffline = (creditInfo?: CreditInfo): boolean => {
  return creditInfo?.companyMdmDetails?.idStatus?.toUpperCase() !== 'ACTIVE';
};

/**
 * Gets address lines from credit info
 * @param creditInfo - Credit information with MDM details
 * @returns Formatted address lines
 */
export const getAddressFromCreditInfo = (creditInfo?: CreditInfo): AddressLines => {
  return creditInfo?.companyMdmDetails?.address
    ? formatMdmAddress(creditInfo.companyMdmDetails.address)
    : { line1: '', line2: '', line3: '' };
};

/**
 * Validates credit amount input
 * @param value - Input value as string
 * @returns Boolean indicating if value is valid
 */
export const isValidCreditAmount = (value: string): boolean => {
  const numericValue = parseFloat(value);
  return !isNaN(numericValue) && numericValue > 0;
};


/**
 * Determines if credit input should be shown
 * @param creditTrackingMode - Current credit tracking mode
 * @param isConnected - Connection status
 * @returns Boolean indicating if input should be shown
 */
export const shouldShowCreditInput = (
  creditTrackingMode: CreditTrackingMode,
): boolean => {
  return creditTrackingMode === CreditTrackingMode.OFMP;
};

/**
 * Determines if track manually button should be shown
 * @param creditTrackingMode - Current credit tracking mode
 * @returns Boolean indicating if button should be shown
 */
export const shouldShowTrackManuallyButton = (
  creditTrackingMode: CreditTrackingMode,
  creditId: string
): boolean => {
  return !creditId ||
    creditTrackingMode === CreditTrackingMode.ERP ||
    creditTrackingMode === CreditTrackingMode.OFFLINE;
};

/**
 * Extract credit information from API response
 */
export const extractCreditInfo = (result: any): { creditInfo: CreditInfo, creditId: string } => {
  if (result.credit) {
    // Credit info from credit object
    return {
      creditInfo: {
        totalCreditLimit: result.credit.amount || 0,
        availableCreditLimit: (result.credit.availableAmount || 0),
        utilization: result.credit.usedAmount || 0,
        isReadOnly: false,
        companyMdmDetails: result.credit.companyMdmDetails
      },
      creditId: result.credit.creditId || ''
    };
  } else {
    return {
      creditInfo: emptyCreditInfo,
      creditId: ''
    };
  }
};

/**
 * Determine credit tracking mode based on source and status
 */
export const determineCreditTrackingMode = (result: any): CreditTrackingMode => {
  if (result.credit) {
    if (result.credit.source === CREDIT_SOURCES.MDM) {
      const isMdmActive = result.credit.companyMdmDetails &&
        result.credit.companyMdmDetails.idStatus &&
        result.credit.companyMdmDetails.idStatus.toUpperCase() === 'ACTIVE';

      return isMdmActive ? CreditTrackingMode.ERP : CreditTrackingMode.OFFLINE;
    } else if (result.credit.source === CREDIT_SOURCES.INTERNAL) {
      return CreditTrackingMode.OFMP;
    }
  }

  return CreditTrackingMode.UID_REQUIRED;
};


export const CREDIT_VALUE_RANGE = {
  MIN: 0,
  MAX: 999000000
};